import React, { useEffect, useRef, useState } from "react";
import { ArrowUpRight16Filled } from "@fluentui/react-icons";
import axios from "axios";
import "./ProjectEmails.css";
import { useAuthFetch } from "../hooks/useAuthFetch";
import { useStandalonePwa } from "../hooks/useStandalonePwa";
import { useNestedScrollChain } from "../hooks/useNestedScrollChain";

// Update the date formatting helper to include timezone handling
const formatDate = (dateString) => {
  const date = new Date(dateString);
  
  // For the list view - just the date in local timezone
  return date.toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
  });
};

// Add a helper for formatting date and time with timezone
const formatDateTime = (dateString) => {
  const date = new Date(dateString);
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  
  return {
    fullDateTime: date.toLocaleString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
      timeZone
    }),
    timeZone: timeZone.replace(/_/g, ' ')
  };
};

function ProjectEmails({ projectData }) {
  const { getAuthConfig, isReady } = useAuthFetch();
  const isStandalonePwa = useStandalonePwa();
  const listRef = useRef(null);
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedEmail, setSelectedEmail] = useState(null);

  useNestedScrollChain(listRef, isStandalonePwa);

  useEffect(() => {
    if (!isReady) {
      setEmails([]);
      setSelectedEmail(null);
      return;
    }

    // Reset previous project state immediately to avoid visual carry-over
    setEmails([]);
    setSelectedEmail(null);
    setError(null);

    if (!projectData?.opportunityUUID) return;

    let cancelled = false;

    (async () => {
      try {
        setLoading(true);
        if (!cancelled) {
          await fetchEmails(projectData.opportunityUUID);
        }
      } catch (err) {
        if (!cancelled) {
          console.error("Error fetching emails:", err);
          setError("Failed to fetch emails.");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isReady, projectData?.opportunityUUID, getAuthConfig]);

  // Add effect to handle refresh events
  useEffect(() => {
    const handleRefresh = async () => {
      if (!isReady) return;
      await fetchEmails(projectData?.opportunityUUID);
    };

    window.addEventListener('refreshProjectEmails', handleRefresh);
    return () => window.removeEventListener('refreshProjectEmails', handleRefresh);
  }, [isReady, projectData?.opportunityUUID, getAuthConfig]);

  const fetchEmails = async (opportunityUUID) => {
    if (!opportunityUUID || !isReady) return;
    
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_BASEURL}/api/emails/${opportunityUUID}`,
        await getAuthConfig()
      );

      if (!response.data?.success) {
        setError("Error fetching emails.");
        setLoading(false);
        return;
      }

      const emailsData = response.data.data || [];
      
      const mapEmail = (email) => {
        const rawJson = email.raw_json || {};
        return {
          id: email.id,
          from: email.from_name || "Unknown",
          fromEmail: email.from_email || "",
          to: (rawJson.toRecipients || []).map(r => ({
            name: r.emailAddress?.name || "",
            email: r.emailAddress?.address || ""
          })),
          cc: (rawJson.ccRecipients || []).map(r => ({
            name: r.emailAddress?.name || "",
            email: r.emailAddress?.address || ""
          })),
          bcc: (rawJson.bccRecipients || []).map(r => ({
            name: r.emailAddress?.name || "",
            email: r.emailAddress?.address || ""
          })),
          time: email.received_at,
          subject: rawJson.subject || "No Subject",
          bodyPreview: rawJson.bodyPreview || "",
          body: rawJson.body?.content || "",
          folder: email.folder || "Inbox",
          isSelected: false,
        };
      };

      if (projectData?.opportunityUUID !== opportunityUUID) {
        return;
      }

      const mappedEmails = emailsData.map(email => mapEmail(email));
      setEmails(mappedEmails);
    } catch (err) {
      console.error("Error fetching emails:", err);
      setError("Failed to fetch emails.");
    }
  };

  return (
    <section className="project-emails p-4">
      <div className="project-emails-header flex justify-between items-center mb-4">
        <h2 className="project-emails-title text-xl font-semibold">Project Emails</h2>
        {/* <button
          className="flex items-center gap-2 text-blue-600 hover:underline"
          onClick={() => window.open("https://outlook.office.com/mail/", "_blank")}
        >
          View All <ArrowUpRight16Filled />
        </button> */}
      </div>

      <div
        ref={listRef}
        className="email-list project-emails-list overflow-y-auto"
        style={{ height: '330px' }}
      >
        {emails.length > 0 ? (
          emails.map((email) => (
            <div
              key={email.id}
              className="project-email-item bg-white rounded-lg shadow-sm p-3 mb-2 border border-gray-200 hover:shadow-md transition cursor-pointer flex items-center gap-3"
              onClick={() => setSelectedEmail(email)}
            >
              <div className="project-email-item-body flex-1 min-w-0">
                <div className="project-email-meta flex items-center gap-2 text-xs text-gray-500 mb-1">
                  <span title={formatDateTime(email.time).fullDateTime}>
                    {formatDate(email.time)}
                  </span>
                  <span>•</span>
                  <span>{email.folder}</span>
                </div>
                
                <div className="project-email-fields grid grid-cols-[auto,1fr] gap-x-2 gap-y-0.5 text-sm">
                  <span className="text-gray-600">From:</span>
                  <span className="project-email-field-value truncate">{email.from}</span>
                  
                  <span className="text-gray-600">To:</span>
                  <span className="project-email-field-value truncate">
                    {email.to.map(r => r.name).join(", ")}
                  </span>
                  
                  {email.cc.length > 0 && (
                    <>
                      <span className="text-gray-600">CC:</span>
                      <span className="project-email-field-value truncate">
                        {email.cc.map(r => r.name).join(", ")}
                      </span>
                    </>
                  )}
                </div>
                
                <h3 className="project-email-subject text-sm font-medium text-gray-800 mt-1 truncate">
                  {email.subject}
                </h3>
              </div>
            </div>
          ))
        ) : (
          <p className="project-emails-empty text-sm text-gray-500">No emails for the selected project.</p>
        )}
      </div>

      {/* Popup Modal */}
      {selectedEmail && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-40 flex items-center justify-center p-4">
          <div className="relative bg-white rounded-2xl shadow-lg w-full max-w-5xl max-h-[90vh] overflow-hidden">
            <button
              onClick={() => setSelectedEmail(null)}
              className="absolute top-3 right-4 text-gray-600 hover:text-black text-2xl font-bold"
              aria-label="Close email preview"
            >
              &times;
            </button>

            <div className="p-6 border-b">
              <h2 className="text-xl font-semibold text-gray-800">{selectedEmail.subject}</h2>
              <p className="text-sm text-gray-500 mt-1">
                {formatDateTime(selectedEmail.time).fullDateTime}
                <span className="ml-1 text-gray-400">
                  ({formatDateTime(selectedEmail.time).timeZone})
                </span>
              </p>
            </div>

            <div className="px-6 pt-4 pb-2 overflow-y-auto max-h-[70vh] space-y-3 text-sm text-gray-700">
              <div><strong>From:</strong> {selectedEmail.from} &lt;{selectedEmail.fromEmail}&gt;</div>
              <div><strong>To:</strong> {selectedEmail.to.map(r => `${r.name} <${r.email}>`).join(", ")}</div>
              {selectedEmail.cc.length > 0 && (
                <div><strong>CC:</strong> {selectedEmail.cc.map(r => `${r.name} <${r.email}>`).join(", ")}</div>
              )}
              {selectedEmail.bcc.length > 0 && (
                <div><strong>BCC:</strong> {selectedEmail.bcc.map(r => `${r.name} <${r.email}>`).join(", ")}</div>
              )}
              <hr className="my-3 border-gray-300" />
              <div
                className="email-body prose max-w-none"
                dangerouslySetInnerHTML={{ __html: selectedEmail.body }}
              />
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

export default ProjectEmails;
